import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function mapInputType(inputType, t) {
  switch (inputType) {
    case 'internal_note': return { type: 'note', title: t('advisorPortal.activityEntryInternalNote') };
    case 'call_inbound': return { type: 'call_inbound', channel: 'phone', title: t('advisorPortal.activityTitleCallInbound') };
    case 'call_outbound': return { type: 'call_outbound', channel: 'phone', title: t('advisorPortal.activityTitleCallOutbound') };
    case 'whatsapp_inbound': return { type: 'message_received', channel: 'whatsapp', title: t('advisorPortal.activityTitleWhatsAppInbound') };
    case 'whatsapp_outbound': return { type: 'message_sent', channel: 'whatsapp', title: t('advisorPortal.activityTitleWhatsAppOutbound') };
    case 'email_inbound': return { type: 'message_received', channel: 'email', title: t('advisorPortal.activityTitleEmailInbound') };
    case 'email_outbound': return { type: 'message_sent', channel: 'email', title: t('advisorPortal.activityTitleEmailOutbound') };
    case 'sms_inbound': return { type: 'message_received', channel: 'sms', title: t('advisorPortal.activityTitleSmsInbound') };
    case 'sms_outbound': return { type: 'message_sent', channel: 'sms', title: t('advisorPortal.activityTitleSmsOutbound') };
    default: return { type: 'note', title: t('advisorPortal.historyLogType_note') };
  }
}

let _entrySeq = 0;

function NoteModal({
  initialText,
  inputType,
  selectedDoc,
  requiredDocuments,
  onClose,
  onPost,
  onTextChange,
}) {
  const { t } = useTranslation();
  const [verText, setVerText] = useState(initialText);
  const textareaRef = useRef(null);

  useEffect(() => {
    setVerText(initialText);
  }, [initialText]);

  useEffect(() => {
    const timeoutId = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = () => {
    if (!verText.trim()) return;
    onTextChange(verText);
    onClose();
    onPost(verText, inputType, selectedDoc);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-3 bg-black/30 backdrop-blur-[2px] sm:items-center sm:p-6">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl flex flex-col overflow-hidden max-h-[calc(100dvh-1.5rem)] sm:min-h-[420px] sm:max-h-[80vh]">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-800 flex-1">{t('advisorPortal.addNoteTitle')}</span>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line text-base" />
          </button>
        </div>
        <div className="flex-1 px-5 pb-4 flex flex-col min-h-0">
          <textarea
            ref={textareaRef}
            value={verText}
            onChange={(e) => setVerText(e.target.value)}
            placeholder={t('advisorPortal.activityWriteNotePlaceholder')}
            className="flex-1 w-full resize-none bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:bg-white transition-colors leading-relaxed"
            style={{ minHeight: '220px' }}
          />
        </div>
        <div className="flex flex-col-reverse gap-2 px-5 py-3.5 border-t border-slate-100 bg-slate-50/60 min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-end">
          <button onClick={onClose} className="min-h-[44px] px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap">{t('advisorPortal.sectionCancel')}</button>
          <button onClick={handleSubmit} disabled={!verText.trim()} className="min-h-[44px] px-5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap">{t('advisorPortal.sectionSave')}</button>
        </div>
      </div>
    </div>
  );
}

export default function ActivityInputBar({
  requiredDocuments,
  onPost,
  advisorName = 'Advisor',
  caseId = null,
  className = '',
  variant = 'inline',
  hideTypeSelector = false,
  posting = false,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [inputType, setInputType] = useState('internal_note');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [typeOpen, setTypeOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const textareaRef = useRef(null);
  const typeDropRef = useRef(null);
  const docDropRef = useRef(null);

  const entryTypeOptions = useMemo(() => ([
    { value: 'internal_note', label: t('advisorPortal.activityEntryInternalNote'), icon: 'ri-sticky-note-line', color: 'text-slate-500' },
    { value: 'call_inbound', label: t('advisorPortal.activityEntryCallInbound'), icon: 'ri-phone-fill', color: 'text-emerald-600' },
    { value: 'call_outbound', label: t('advisorPortal.activityEntryCallOutbound'), icon: 'ri-phone-line', color: 'text-teal-600' },
    { value: 'whatsapp_inbound', label: t('advisorPortal.activityEntryWhatsAppInbound'), icon: 'ri-whatsapp-line', color: 'text-emerald-600' },
    { value: 'whatsapp_outbound', label: t('advisorPortal.activityEntryWhatsAppOutbound'), icon: 'ri-whatsapp-line', color: 'text-teal-600' },
    { value: 'email_inbound', label: t('advisorPortal.activityEntryEmailInbound'), icon: 'ri-mail-download-line', color: 'text-slate-500' },
    { value: 'email_outbound', label: t('advisorPortal.activityEntryEmailOutbound'), icon: 'ri-mail-send-line', color: 'text-teal-600' },
    { value: 'sms_inbound', label: t('advisorPortal.activityEntrySmsInbound'), icon: 'ri-message-2-line', color: 'text-slate-500' },
    { value: 'sms_outbound', label: t('advisorPortal.activityEntrySmsOutbound'), icon: 'ri-send-plane-line', color: 'text-teal-600' },
  ]), [t]);

  const maxHeightPx = variant === 'stacked' ? 130 : 110;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeightPx)}px`;
  }, [text, maxHeightPx]);

  useEffect(() => {
    const handler = (e) => {
      if (typeDropRef.current && !typeDropRef.current.contains(e.target)) setTypeOpen(false);
      if (docDropRef.current && !docDropRef.current.contains(e.target)) setDocOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentOption = entryTypeOptions.find((o) => o.value === inputType) || entryTypeOptions[0];

  const handlePost = useCallback((postText = text, postInputType = inputType, postDoc = selectedDoc) => {
    const trimmed = postText.trim();
    if (!trimmed || posting) return;
    const { type, channel, title } = mapInputType(postInputType, t);
    _entrySeq += 1;
    const entry = {
      id: `posted-${Date.now()}-${_entrySeq}`,
      datetime: new Date().toISOString(),
      type,
      title,
      detail: trimmed,
      text: trimmed,
      agent: advisorName,
      channel,
      caseId,
      entryInputType: postInputType,
      ...(postDoc ? { documentTag: postDoc.name } : {}),
    };
    onPost?.(entry);
    setText('');
    setSelectedDoc(null);
    setDocOpen(false);
    setNoteModalOpen(false);
    // For internal notes, focusing the readonly textarea re-triggers onFocus and reopens the modal.
    if (postInputType !== 'internal_note') {
      textareaRef.current?.focus();
    }
  }, [text, inputType, selectedDoc, caseId, advisorName, onPost, posting, t]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePost();
    }
  };

  const handleTextareaFocus = () => {
    if (inputType === 'internal_note') setNoteModalOpen(true);
  };

  const showDocTag = requiredDocuments && requiredDocuments.length > 0;

  const noteModal = noteModalOpen ? (
    <NoteModal
      initialText={text}
      inputType={inputType}
      selectedDoc={selectedDoc}
      requiredDocuments={requiredDocuments}
      onClose={() => setNoteModalOpen(false)}
      onPost={(postText, postInputType, doc) => handlePost(postText, postInputType, doc)}
      onTextChange={setText}
    />
  ) : null;

  if (variant === 'stacked') {
    return (
      <>
        {noteModal}
        <div className={`w-full space-y-2 ${className}`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 relative" ref={typeDropRef}>
              <button
                onClick={() => { setTypeOpen((v) => !v); setDocOpen(false); }}
                className="w-full flex items-center gap-2 min-h-[40px] px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                title={t('advisorPortal.activityEntryTypeAria')}
              >
                <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${currentOption.color}`}>
                  <i className={`${currentOption.icon} text-sm`} />
                </span>
                <span className="flex-1 text-left truncate">{currentOption.label}</span>
                <span className="w-3 h-3 flex items-center justify-center text-slate-400 shrink-0">
                  <i className={`${typeOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`} />
                </span>
              </button>
              {typeOpen && (
                  <div className="absolute top-full mt-1 left-0 right-0 z-40 bg-white border border-slate-200 rounded-xl py-1.5 overflow-hidden">
                  {entryTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setInputType(opt.value); setTypeOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors cursor-pointer ${
                        inputType === opt.value ? 'bg-slate-50 font-semibold text-slate-800' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${opt.color}`}>
                        <i className={`${opt.icon} text-sm`} />
                      </span>
                      {opt.label}
                      {inputType === opt.value ? (
                        <span className="ml-auto w-3 h-3 flex items-center justify-center">
                          <i className="ri-check-line text-xs text-slate-400" />
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {showDocTag ? (
              <div className="relative shrink-0" ref={docDropRef}>
                <button
                  onClick={() => { setDocOpen((v) => !v); setTypeOpen(false); }}
                  className={`h-8 w-8 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                    docOpen || selectedDoc
                      ? 'bg-teal-50 border-teal-200 text-teal-600'
                      : 'bg-transparent border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'
                  }`}
                  title={t('advisorPortal.activityDocTagAria')}
                >
                  <i className="ri-attachment-2 text-sm" />
                </button>
                {docOpen && (
                  <div className="absolute top-full mt-1 right-0 z-40 bg-white border border-slate-200 rounded-xl w-60 py-1.5 overflow-hidden">
                    <p className="px-3 pt-1 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100">
                      {t('advisorPortal.activityDocTagTitle')}
                    </p>
                    {requiredDocuments.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => { setSelectedDoc(selectedDoc?.id === doc.id ? null : doc); setDocOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors cursor-pointer ${
                          selectedDoc?.id === doc.id ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                          <i className={`${selectedDoc?.id === doc.id ? 'ri-checkbox-circle-fill text-teal-500' : 'ri-file-line text-slate-400'} text-xs`} />
                        </span>
                        <span className="truncate">{doc.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {selectedDoc ? (
            <div className="flex items-center gap-1 w-fit pl-2 pr-1.5 py-0.5 bg-teal-50 border border-teal-200 rounded-md">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-attachment-2 text-teal-600 text-xs" />
              </span>
              <span className="text-xs font-medium text-teal-700 whitespace-nowrap max-w-[180px] truncate">{selectedDoc.name}</span>
              <button
                onClick={() => setSelectedDoc(null)}
                className="ml-0.5 w-4 h-4 flex items-center justify-center text-teal-400 hover:text-teal-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          ) : null}

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={handleTextareaFocus}
            onKeyDown={handleKeyDown}
            rows={2}
            placeholder={inputType === 'internal_note' ? t('advisorPortal.activityClickToWrite') : t('advisorPortal.activityNotePlaceholder')}
            readOnly={inputType === 'internal_note'}
            className={`w-full resize-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 focus:bg-white transition-colors leading-relaxed ${inputType === 'internal_note' ? 'cursor-pointer' : ''}`}
            style={{ minHeight: '60px', maxHeight: `${maxHeightPx}px`, overflow: 'auto' }}
          />

          {inputType !== 'internal_note' ? (
            <div className="flex justify-end">
              <button
                onClick={() => handlePost()}
                disabled={!text.trim() || posting}
                className="min-h-[40px] px-4 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
              >
                {posting ? t('advisorPortal.activityPostPosting') : t('advisorPortal.activityPost')}
              </button>
            </div>
          ) : (
            <div className="flex justify-start">
              <button onClick={() => setNoteModalOpen(true)} className="min-h-[40px] px-4 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700 transition-all cursor-pointer whitespace-nowrap">
                {t('advisorPortal.add')}
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {noteModal}
      <div className={`flex flex-col gap-2 sm:flex-row sm:items-center ${className}`}>
        {!hideTypeSelector && (
          <div className="relative shrink-0" ref={typeDropRef}>
            <button
              onClick={() => { setTypeOpen((v) => !v); setDocOpen(false); }}
              className="flex min-h-[40px] items-center gap-1.5 px-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap"
              title={t('advisorPortal.activityEntryTypeAria')}
            >
              <span className={`w-4 h-4 flex items-center justify-center ${currentOption.color}`}>
                <i className={`${currentOption.icon} text-sm`} />
              </span>
              <span className="max-w-[100px] truncate hidden sm:block">{currentOption.label}</span>
              <span className="w-3 h-3 flex items-center justify-center text-slate-400">
                <i className="ri-arrow-up-s-line text-xs" />
              </span>
            </button>
            {typeOpen && (
              <div className="absolute bottom-full mb-1.5 left-0 z-40 bg-white border border-slate-200 rounded-xl w-[min(13rem,calc(100vw-2rem))] py-1.5 overflow-hidden">
                {entryTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setInputType(opt.value); setTypeOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors cursor-pointer ${inputType === opt.value ? 'bg-slate-50 font-semibold text-slate-800' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <span className={`w-4 h-4 flex items-center justify-center shrink-0 ${opt.color}`}>
                      <i className={`${opt.icon} text-sm`} />
                    </span>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {showDocTag && (
          <div className="relative shrink-0" ref={docDropRef}>
            <button
              onClick={() => { setDocOpen((v) => !v); setTypeOpen(false); }}
              className={`h-8 w-8 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${
                docOpen || selectedDoc
                  ? 'bg-teal-50 border-teal-200 text-teal-600'
                  : 'bg-transparent border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'
              }`}
              title={t('advisorPortal.activityDocTagAria')}
            >
              <i className="ri-attachment-2 text-sm" />
            </button>
            {docOpen && (
              <div className="absolute bottom-full mb-1.5 left-0 z-40 bg-white border border-slate-200 rounded-xl w-[min(15rem,calc(100vw-2rem))] py-1.5 overflow-hidden">
                <p className="px-3 pt-1 pb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100">
                  {t('advisorPortal.activityDocTagTitle')}
                </p>
                {requiredDocuments.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => { setSelectedDoc(selectedDoc?.id === doc.id ? null : doc); setDocOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors cursor-pointer ${
                      selectedDoc?.id === doc.id ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                      <i className={`${selectedDoc?.id === doc.id ? 'ri-checkbox-circle-fill text-teal-500' : 'ri-file-line text-slate-400'} text-xs`} />
                    </span>
                    <span className="truncate">{doc.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={handleTextareaFocus}
            onKeyDown={handleKeyDown}
            rows={1}
            readOnly={inputType === 'internal_note'}
            placeholder={inputType === 'internal_note' ? t('advisorPortal.activityClickToWrite') : t('advisorPortal.activityNotePlaceholder')}
            className={`w-full resize-none bg-slate-50 border border-slate-200 rounded-lg px-3 h-8 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 focus:bg-white transition-colors ${inputType === 'internal_note' ? 'cursor-pointer' : ''}`}
            style={{ maxHeight: `${maxHeightPx}px`, overflow: 'auto', lineHeight: '30px' }}
          />
        </div>

        {selectedDoc && (
          <div className="flex items-center gap-1 h-8 pl-2 pr-1.5 bg-teal-50 border border-teal-200 rounded-lg shrink-0">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-attachment-2 text-teal-600 text-xs" />
            </span>
            <span className="text-xs font-medium text-teal-700 whitespace-nowrap max-w-[90px] truncate">{selectedDoc.name}</span>
            <button
              onClick={() => setSelectedDoc(null)}
              className="ml-0.5 w-4 h-4 flex items-center justify-center text-teal-400 hover:text-teal-700 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        )}

        <button
          onClick={inputType === 'internal_note' ? () => setNoteModalOpen(true) : () => handlePost()}
          disabled={posting || (inputType !== 'internal_note' && !text.trim())}
          className="min-h-[40px] px-3.5 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap shrink-0"
        >
          {inputType === 'internal_note' ? t('advisorPortal.add') : (posting ? t('advisorPortal.activityPostPosting') : t('advisorPortal.activityPost'))}
        </button>
      </div>
    </>
  );
}
